import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';

interface AdminQuest {
  id: string;
  learningDay: string;
  key: string;
  area: string;
  status: string;
  note: string | null;
  title: string;
  task: string;
  forGrownUps: string;
  confirmed: boolean;
}

interface ChildQuests {
  childId: string;
  nickname: string;
  done: number;
  total: number;
  quests: AdminQuest[];
}

/**
 * The quest log, for a parent.
 *
 * Deliberately not a compliance report. There is no completion percentage in a
 * traffic-light colour, because the number that matters is not how many got
 * ticked - it is whether the two of you counted the pegs together once this
 * week. A dashboard that scores a family on that would make it a chore.
 */
export function AdminQuests() {
  const queryClient = useQueryClient();
  const data = useQuery({
    queryKey: ['admin', 'quests'],
    queryFn: () => api<{ children: ChildQuests[] }>('/admin/quests?days=60'),
  });

  const confirm = useMutation({
    mutationFn: (questId: string) => api(`/admin/quests/${questId}/confirm`, { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'quests'] }),
  });

  return (
    <main className="screen admin">
      <h1>Real-world quests</h1>
      <p className="muted">
        One short thing a day to do away from the screen &mdash; counting the pegs, reading the
        pantry, working out the change. Nothing verifies them and nothing is lost by skipping one.
        The tick below is just a record that you did it together.
      </p>

      {data.isPending && <p>Loading&hellip;</p>}

      {data.data?.children.map((child) => (
        <section key={child.childId} className="card">
          <h2>
            {child.nickname}
            <span className="pill">
              {child.done} of {child.total} done
            </span>
          </h2>

          {child.quests.length === 0 && (
            <p className="muted">
              Nothing yet. A quest appears on {child.nickname}&rsquo;s home screen each day.
            </p>
          )}

          <ul className="quest-list">
            {child.quests.map((quest) => (
              <li key={quest.id}>
                <div>
                  <strong>{quest.learningDay}</strong> &mdash; {quest.title}
                  <span className="pill">{quest.area === 'English' ? 'English' : 'Maths'}</span>
                  {quest.status === 'done' ? (
                    <span className="pill">done</span>
                  ) : (
                    <span className="muted"> not ticked off</span>
                  )}
                  <p className="muted">{quest.task}</p>
                  {quest.forGrownUps && (
                    <p className="muted">
                      <em>What it&rsquo;s for:</em> {quest.forGrownUps}
                    </p>
                  )}
                  {quest.note && <blockquote className="child-words">{quest.note}</blockquote>}
                </div>
                <button
                  type="button"
                  className={quest.confirmed ? 'btn btn--quiet' : 'btn btn--outline'}
                  disabled={confirm.isPending}
                  onClick={() => confirm.mutate(quest.id)}
                >
                  {quest.confirmed ? '✓ We did this together' : 'We did this together'}
                </button>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </main>
  );
}
